(() => {
  const SELECTOR = '[data-prime-cart-upsell]';
  const STATE_KEY = 'primeCartUpsellStateV22';

  let pendingStartProgress = null;
  let pendingStartCount = null;

  /*
    Keep the last VISIBLE state in memory.
    This is the key fix: quantity +/- and other cart re-renders do not pass
    through our Añadir + handler, but this variable survives DOM replacement.
  */
  let lastVisibleProgress = null;
  let lastVisibleCount = null;

  const animateProgress = (root, forcedStartProgress = null, forcedStartCount = null) => {
    const fill = root.querySelector('[data-prime-progress-fill]');
    const message = root.querySelector('[data-prime-message]');
    if (!fill) return;

    const count = Number(root.dataset.primeCount || 0);
    const target = Math.max(0, Math.min(100, Number(root.dataset.primeProgress || 0)));

    const startProgress = forcedStartProgress !== null
      ? Math.max(0, Math.min(100, Number(forcedStartProgress)))
      : target;

    const startCount = forcedStartCount !== null
      ? Number(forcedStartCount)
      : count;

    /* Important: paint the OLD position first. */
    fill.style.transition = 'none';
    fill.style.setProperty('width', `${startProgress}%`, 'important');
    fill.style.setProperty('--prime-progress', `${startProgress}%`);
    fill.classList.remove('is-moving');

    if (message && startCount !== count) {
      message.classList.remove('is-changing');
      void message.offsetWidth;
      message.classList.add('is-changing');
    }

    /* Force layout so the browser really displays the old state. */
    void fill.offsetWidth;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        fill.style.transition = '';
        fill.classList.add('is-moving');

        /* We animate width directly so Shopify's re-render cannot skip the trip. */
        fill.style.setProperty('width', `${target}%`, 'important');
        fill.style.setProperty('--prime-progress', `${target}%`);

        window.setTimeout(() => {
          fill.classList.remove('is-moving');
        }, 1000);
      });
    });

    [3, 4].forEach((milestone) => {
      const el = root.querySelector(`[data-prime-milestone="${milestone}"]`);
      if (!el) return;

      const unlockedNow = count >= milestone;
      const unlockedBefore = startCount >= milestone;

      el.classList.toggle('is-unlocked', unlockedNow);
      const dot = el.querySelector('.prime-cart-upsell__dot');
      if (dot) dot.textContent = unlockedNow ? '✓' : '';

      if (unlockedNow && !unlockedBefore) {
        window.setTimeout(() => {
          el.classList.remove('just-unlocked');
          void el.offsetWidth;
          el.classList.add('just-unlocked');
          window.setTimeout(() => el.classList.remove('just-unlocked'), 700);
        }, 720);
      }
    });
  };

  const getDrawer = () => document.querySelector('cart-drawer');

  const scrollCarousel = (root, direction) => {
    const scroller = root.querySelector('[data-prime-upsell-scroller]');
    if (!scroller) return;
    const card = scroller.querySelector('.prime-cart-upsell__card');
    const amount = card ? card.getBoundingClientRect().width + 8 : scroller.clientWidth * 0.85;
    scroller.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  const addVariant = async (root, button) => {
    if (button.disabled) return;
    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const label = button.querySelector('[data-prime-add-label]');
    const originalLabel = label ? label.textContent : 'Añadir +';

    /* Save exactly where the visible bar is BEFORE Shopify replaces the drawer. */
    const oldCount = Number(root.dataset.primeCount || 0);
    const oldProgress = Number(root.dataset.primeProgress || 0);
    pendingStartCount = oldCount;
    pendingStartProgress = oldProgress;
    button.disabled = true;
    if (label) label.textContent = 'Añadiendo…';

    try {
      const drawer = getDrawer();
      const sections = drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map((section) => section.id)
        : [];

      const payload = { items: [{ id: variantId, quantity: 1 }] };
      if (sections.length) {
        payload.sections = sections;
        payload.sections_url = window.location.pathname;
      }

      const rootPath = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const response = await fetch(`${rootPath}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'No se pudo añadir el producto.');

      if (drawer && data.sections && typeof drawer.renderContents === 'function') {
        drawer.renderContents(data);
        if (typeof drawer.open === 'function') drawer.open();

        /*
          The new Liquid arrives already containing its final percentage.
          We immediately put it back at the previous percentage, force a paint,
          then animate forward to the new target.
        */
        window.setTimeout(() => {
          initAll();
        }, 30);
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        if (label) label.textContent = 'Añadido ✓';
        window.setTimeout(() => {
          button.disabled = false;
          if (label) label.textContent = originalLabel;
        }, 900);
      }
    } catch (error) {
      console.error('[Prime Cart Upsell]', error);
      button.disabled = false;
      if (label) label.textContent = 'Reintentar';
      window.setTimeout(() => {
        if (label) label.textContent = originalLabel;
      }, 1400);
    }
  };

  const initRoot = (root) => {
    if (!root) return;

    const currentProgress = Number(root.dataset.primeProgress || 0);
    const currentCount = Number(root.dataset.primeCount || 0);

    if (root.dataset.primeUpsellReady === 'true') {
      return;
    }

    root.dataset.primeUpsellReady = 'true';

    /*
      If Shopify replaced the entire cart drawer, animate the NEW bar from the
      last state that the customer actually saw. This works for:
      - Añadir + in our carousel
      - quantity + / -
      - other theme cart refreshes
    */
    const startProgress = pendingStartProgress !== null
      ? pendingStartProgress
      : (lastVisibleProgress !== null ? lastVisibleProgress : currentProgress);

    const startCount = pendingStartCount !== null
      ? pendingStartCount
      : (lastVisibleCount !== null ? lastVisibleCount : currentCount);

    animateProgress(root, startProgress, startCount);

    lastVisibleProgress = currentProgress;
    lastVisibleCount = currentCount;
    pendingStartProgress = null;
    pendingStartCount = null;

    root.addEventListener('click', (event) => {
      const prev = event.target.closest('[data-prime-upsell-prev]');
      if (prev && root.contains(prev)) {
        event.preventDefault();
        scrollCarousel(root, -1);
        return;
      }

      const next = event.target.closest('[data-prime-upsell-next]');
      if (next && root.contains(next)) {
        event.preventDefault();
        scrollCarousel(root, 1);
        return;
      }

      const add = event.target.closest('[data-prime-upsell-add]');
      if (add && root.contains(add)) {
        event.preventDefault();
        event.stopPropagation();
        addVariant(root, add);
      }
    });
  };

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);

  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) =>
        node.nodeType === 1 && (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR))
      )
    )) {
      window.setTimeout(initAll, 20);
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
